/**
 * Platform reality for web push (PLAN slice 13b, item 3). These are the checks
 * that decide whether a working push control can exist, or whether the honest
 * thing is a message instead of a button. Pure functions of the environment so
 * they are unit-testable without a real device.
 */

/** iOS/iPadOS. iPadOS 13+ reports as `MacIntel` with touch points, so the platform
 *  check is the second half. This matters because iOS Safari exposes `PushManager`
 *  ONLY inside a home-screen install (iOS 16.4+): in a normal tab it is absent, so
 *  a bare `'PushManager' in window` check would make the control silently vanish on
 *  iOS instead of telling the user to install — the exact silent break item 3
 *  forbids. We must know it is iOS to say so. */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ masquerading as desktop Safari.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/** Running as an installed PWA (home-screen / standalone), not a browser tab. iOS
 *  uses the legacy `navigator.standalone`; everyone else uses the display-mode media
 *  query. Web push on iOS is delivered ONLY in this mode. */
export function isStandalone(): boolean {
  if (typeof navigator !== 'undefined') {
    const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone;
    if (iosStandalone === true) return true;
  }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(display-mode: standalone)').matches;
  }
  return false;
}

/** The browser exposes the APIs a push subscription needs. On iOS this is only
 *  true inside a standalone install (see `isIOS`). */
export function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * The single state the push UI renders from. Resolved from platform + config so the
 * component itself carries no platform branching:
 *   - `unavailable`   → render NOTHING (absent, not disabled): VAPID unconfigured
 *                       (AC-12) OR a browser that cannot do push at all.
 *   - `needs-install` → iOS in a browser tab: state that installing to the home
 *                       screen is required (AC-13). Never a dead button.
 *   - `ready`         → a working control can be shown.
 */
export type PushAvailability = 'unavailable' | 'needs-install' | 'ready';

export function resolvePushAvailability(opts: {
  configured: boolean;
  ios: boolean;
  standalone: boolean;
  supported: boolean;
}): PushAvailability {
  if (!opts.configured) return 'unavailable';
  if (opts.ios && !opts.standalone) return 'needs-install';
  if (!opts.supported) return 'unavailable';
  return 'ready';
}
